import { useState, useEffect, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from '../components/feature/Header';
import Footer from '../components/feature/Footer';
import SuccessSkeleton from '../components/feature/SuccessSkeleton';
import ProgressTracker, { type ProgressTrackerStep } from '../components/feature/ProgressTracker';
import { apiFetch } from '../lib/apiClient';
import type { OrderResource } from '../types/api';
import { buildEcommerceItem, trackEcommerceEvent, trackEvent } from '../lib/analytics';

type UiStatus = 'pending' | 'in_progress' | 'finished' | 'failed' | 'canceled';

const TERMINAL_STATUSES: ReadonlyArray<UiStatus> = ['finished', 'failed', 'canceled'];
const POLL_INTERVAL_MS = 12_000;

const TRACKER_STEP: Record<UiStatus, ProgressTrackerStep> = {
  pending: 'in_queue',
  in_progress: 'registering',
  finished: 'active',
  failed: 'failed',
  canceled: 'failed',
};

// Initial countdown duration shown on the status card; statuses without an entry show no timer.
const COUNTDOWN_SECONDS: Partial<Record<UiStatus, number>> = {
  pending: 600,
  in_progress: 120,
};

function formatCountdown(totalSeconds: number): string {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function useCountdown(seconds: number | null): number | null {
  const [remaining, setRemaining] = useState(seconds);

  useEffect(() => {
    setRemaining(seconds);
    if (seconds == null) return;
    const id = setInterval(() => {
      setRemaining((r) => (r == null ? r : Math.max(0, r - 1)));
    }, 1000);
    return () => clearInterval(id);
  }, [seconds]);

  return remaining;
}

const STATUS_CONTENT: Record<UiStatus, {
  titleKey: string;
  subtitleKey: string | null;
  statusTitleKey: string;
  statusTextKey: string;
  statusNoteKey: string | null;
  statusStyle: string;
  titleStyle: string;
  icon: string;
}> = {
  pending: {
    titleKey: 'payment.received.title',
    subtitleKey: 'payment.received.pendingSubtitle',
    statusTitleKey: 'payment.queue.title',
    statusTextKey: 'payment.queue.desc',
    statusNoteKey: 'payment.queue.wait',
    statusStyle: 'border-[#5625b0] bg-[#ede9fd] text-[#5625b0]',
    titleStyle: 'text-[#111827]',
    icon: 'ri-check-double-line',
  },
  in_progress: {
    titleKey: 'payment.received.title',
    subtitleKey: 'payment.received.pendingSubtitle',
    statusTitleKey: 'payment.registering.title',
    statusTextKey: 'payment.registering.desc',
    statusNoteKey: 'payment.registering.wait',
    statusStyle: 'border-[#2200ce] bg-[#e3f2fd] text-[#2200ce]',
    titleStyle: 'text-[#111827]',
    icon: 'ri-check-double-line',
  },
  finished: {
    titleKey: 'success.title',
    subtitleKey: 'payment.received.pendingSubtitle',
    statusTitleKey: 'payment.success.title',
    statusTextKey: 'payment.success.desc',
    statusNoteKey: null,
    statusStyle: 'border-[#329e3d] bg-[#eefdf5] text-[#329e3d]',
    titleStyle: 'text-[#111827]',
    icon: 'ri-check-double-line',
  },
  failed: {
    titleKey: 'payment.failed.title',
    subtitleKey: 'payment.failed.subtitle',
    statusTitleKey: 'payment.failed.statusTitle',
    statusTextKey: 'payment.failed.desc',
    statusNoteKey: null,
    statusStyle: 'border-[#F00] bg-[#FFEDED] text-[#F00]',
    titleStyle: 'text-[#111827]',
    icon: 'ri-alert-line',
  },
  canceled: {
    titleKey: 'payment.canceled.title',
    subtitleKey: 'payment.canceled.subtitle',
    statusTitleKey: 'payment.canceled.statusTitle',
    statusTextKey: 'payment.canceled.statusText',
    statusNoteKey: null,
    statusStyle: 'border-[#EC0004] bg-[#FFEDED] text-[#EC0004]',
    titleStyle: 'text-[#111827]',
    icon: 'ri-alert-line',
  },
};

function mapOrderToUiStatus(order: OrderResource): UiStatus {
  if (order.payment_status === 'failed') return 'failed';
  if (order.payment_status === 'canceled') return 'canceled';
  if (order.payment_status === 'paid') {
    switch (order.fulfillment_status) {
      case 'finished': return 'finished';
      case 'failed': return 'failed';
      case 'canceled': return 'canceled';
      case 'in_progress': return 'in_progress';
      case 'pending':
      case 'awaiting_payment':
      default: return 'pending';
    }
  }
  return 'pending';
}

export default function SuccessPage() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const [order, setOrder] = useState<OrderResource | null>(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const stoppedRef = useRef(false);
  const purchaseFiredRef = useRef(false);
  const paymentFailedFiredRef = useRef(false);

  const orderId = params.get('orderId') ?? params.get('order');

  useEffect(() => {
    if (!orderId) {
      setFetchError('Order id is missing in the URL.');
      setLoading(false);
      return;
    }

    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    stoppedRef.current = false;

    const fetchOrder = async () => {
      try {
        const result = await apiFetch<{ data: OrderResource }>(`/v1/orders/${orderId}`);
        if (stoppedRef.current) return;

        setOrder(result.data);
        setFetchError(null);
        setLoading(false);

        if (result.data.payment_status === 'paid' && !purchaseFiredRef.current && result.data.product) {
          purchaseFiredRef.current = true;
          const p = result.data.product;
          trackEcommerceEvent('purchase', [buildEcommerceItem({
            countryCode: p.country,
            countryName: p.country,
            period: p.period,
            price: parseFloat(p.price),
            currency: p.currency,
            productId: p.id,
          })], {
            transaction_id: result.data.id,
            value: parseFloat(p.price),
            currency: p.currency,
          });
        }

        if (
          (result.data.payment_status === 'failed' || result.data.payment_status === 'canceled') &&
          !paymentFailedFiredRef.current
        ) {
          paymentFailedFiredRef.current = true;
          trackEvent('payment_failed', {
            error_type: result.data.payment_status === 'canceled' ? 'cancelled' : 'declined',
            value: parseFloat(result.data.product?.price ?? result.data.payment?.amount ?? '0'),
            currency: result.data.product?.currency ?? result.data.payment?.currency ?? 'EUR',
          });
        }

        const uiStatus = mapOrderToUiStatus(result.data);
        if (!TERMINAL_STATUSES.includes(uiStatus)) {
          timeoutId = setTimeout(fetchOrder, POLL_INTERVAL_MS);
        }
      } catch (e) {
        if (stoppedRef.current) return;
        const err = e as { status?: number };
        if (err.status === 404) {
          setFetchError('Order not found.');
          setLoading(false);
          return;
        }
        timeoutId = setTimeout(fetchOrder, POLL_INTERVAL_MS);
      }
    };

    fetchOrder();

    return () => {
      stoppedRef.current = true;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [orderId]);

  const status = order ? mapOrderToUiStatus(order) : null;
  const countdown = useCountdown(status ? COUNTDOWN_SECONDS[status] ?? null : null);
  const timedOut = countdown === 0;

  const timeoutFiredRef = useRef(false);
  useEffect(() => {
    if (countdown !== 0 || !order || timeoutFiredRef.current) return;
    timeoutFiredRef.current = true;
    trackEvent('payment_failed', {
      error_type: 'timeout',
      value: parseFloat(order.product?.price ?? order.payment?.amount ?? '0'),
      currency: order.product?.currency ?? order.payment?.currency ?? 'EUR',
    });
  }, [countdown, order]);

  if (loading) return <SuccessSkeleton />;

  if (fetchError || !order) {
    return (
      <div className="min-h-screen flex flex-col bg-bg">
        <Header />
        <main className="flex-1 flex items-center justify-center px-5 py-16">
          <div className="mx-auto w-full max-w-md text-center">
            <h2 className="mb-3 text-[24px] font-bold text-[#111827]">
              {t('success.errorTitle', { defaultValue: 'Could not load order' })}
            </h2>
            <p className="text-[#374151] mb-6">{fetchError ?? 'Unknown error'}</p>
            <Link
              to="/"
              className="inline-flex items-center text-white justify-center rounded-lg bg-primary px-6 py-3 text-[14px] font-medium hover:border-primary"
            >
              {t('success.backHome', { defaultValue: 'Back to home' })}
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const content = STATUS_CONTENT[status!];
  const title = t(content.titleKey);

  const country = order.product?.country ?? '';
  const period = order.product?.period ?? '';
  const total = order.product?.price ?? order.payment?.amount ?? '';
  const currency = order.product?.currency ?? order.payment?.currency ?? 'EUR';
  const plate = order.license_plate;
  const regCountry = order.country_registration;
  const email = order.email;

  const subtitleEmail = status === 'failed' && email ? email : null;
  const subtitle = subtitleEmail
    ? t('payment.failed.subtitle')
    : content.subtitleKey ? t(content.subtitleKey) : null;

  return (
    <div className="min-h-screen flex flex-col bg-bg">
      <Header />
      <main className="flex-1 flex items-center justify-center px-5 py-16">
        <div className="mx-auto w-full max-w-md text-center">
          <div className="mb-[24px] flex justify-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[#ECFDF5]">
              <i className={`${content.icon} text-[32px] text-[#059669]`} />
            </div>
          </div>

          <h2 className={`mb-3 text-[34px] font-bold tracking-tight ${content.titleStyle}`}>{title}</h2>

          {subtitle && (
            <p className="text-[#374151]">
              {subtitle}
              {subtitleEmail && (
                <>
                  {' '}
                  <span
                    style={{
                      color: '#111827',
                      textAlign: 'center',
                      fontWeight: 600,
                      lineHeight: '24px',
                    }}
                  >
                    {subtitleEmail}
                  </span>
                </>
              )}
            </p>
          )}

          {country && period && (
            <p className="text-[#6B7280] text-sm leading-[21px] my-[4px]">
              {country.toUpperCase()} — {period} {t('success.vignetteNoun')}{regCountry && plate ? ` — ${regCountry} ${plate}` : ''}
            </p>
          )}

          {total && (
            <p className="mb-8 text-[#059669] text-sm leading-[21px] font-semibold">
              {t('success.paid', { amount: `${total} ${currency}` })}
            </p>
          )}

          <div className="mb-6">
            <ProgressTracker step={TRACKER_STEP[status!]} />
          </div>

          <div className={`text-left rounded-[18px] border mb-[6px] py-[16px] px-[25px] ${content.statusStyle}`}>
            <div className="flex items-center justify-between gap-3">
              <h3 className="text-[20px] font-bold">{t(content.statusTitleKey)}</h3>
              {countdown != null && <span className="text-[20px] font-black">{formatCountdown(countdown)}</span>}
            </div>
            <p className="mt-2 text-[12px]">{t(content.statusTextKey)}</p>
            {content.statusNoteKey && (
              <p className="text-[12px] font-semibold mt-[6px]">{t(content.statusNoteKey)}</p>
            )}
            {timedOut && (
              <p className="text-[12px] font-semibold mt-[6px]">
                {t('payment.overtime.note', { defaultValue: "This is taking longer than usual. We're still working on it — no action is needed." })}
              </p>
            )}
            {status === 'finished' && (
              <>
                <div className="my-3 h-px w-full bg-current" />
                <div className="flex items-start gap-2.5">
                  <i className="ri-alert-line text-[14px]" />
                  <p className="text-[10px] leading-[12px]">{t('payment.success.emailNote')}</p>
                </div>
              </>
            )}
          </div>

          <div className="mb-6 rounded-xl border border-border bg-surface p-5 text-left text-[14px] text-muted">
            <p className="mb-[12px] font-semibold text-ink">{t('success.whatNext')}</p>
            <ul className="mt-2 space-y-1.5 list-none">
              <li className="flex items-center gap-[12px]">
                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary-soft text-primary">
                  <i className="ri-mail-line text-[11px]" />
                </span>
                {t('success.next1')}
              </li>
              <li className="flex items-center gap-[12px]">
                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary-soft text-primary">
                  <i className="ri-file-list-3-line text-[11px]" />
                </span>
                {t('success.next2')}
              </li>
              <li className="flex items-center gap-[12px]">
                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary-soft text-primary">
                  <i className="ri-road-map-line text-[11px]" />
                </span>
                {t('success.next3')}
              </li>
            </ul>
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:justify-center">
            <Link
              to={country ? `/${country.toLowerCase()}` : '/'}
              className="inline-flex items-center text-white justify-center rounded-lg border border-border bg-primary px-6 py-3 text-[14px] font-medium transition hover:border-primary cursor-pointer"
            >
              {t('success.backHome')}
            </Link>
            <Link
              to="/#countries"
              className="inline-flex items-center justify-center rounded-lg bg-white px-6 py-3 text-[14px] font-semibold text-black transition cursor-pointer border border-[#E5E7EB]"
            >
              {t('success.buyAnother')}
            </Link>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
